import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

interface DownloadMutationOptions {
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

export function useNO3Download(options?: DownloadMutationOptions) {
  return useMutation({
    mutationFn: async (id: number | string): Promise<void> => {
      try {
        const response = await axios.get(`/signatures/no3/${id}`, {
          responseType: 'blob',
        });

        let fileName: string | null = null;
        
        // Try to extract the filename from the Content-Disposition header
        const contentDisposition = 
          response.headers['content-disposition'] || 
          response.headers['Content-Disposition'];
        
        if (contentDisposition) {
          // Match the filename in Content-Disposition header
          const filenameMatch = /filename[^;=\n]*=(['"]?)([^"';]+)\1/i.exec(contentDisposition);
          if (filenameMatch && filenameMatch[2]) {
            fileName = decodeURIComponent(filenameMatch[2]);
          }
        }
        
        // If we couldn't get the filename from the header, generate one
        if (!fileName) {
          // Extract the current date in YYYYMMDD format for filename
          const date = new Date();
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const timestamp = String(date.getHours()).padStart(2, '0') + 
                            String(date.getMinutes()).padStart(2, '0') + 
                            String(date.getSeconds()).padStart(2, '0');
          
          // Format filename for NO3 signed forms
          fileName = `NO3_SignedForm_${id}_${year}${month}${day}_${timestamp}.pdf`;
        }
        
        // Determine content type
        const contentType = response.headers['content-type'] || 'application/pdf';
        
        const blob = new Blob([response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading NO3 signed form:', error);
        throw error;
      }
    },
    onSuccess: () => {
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
} 